import type { ReactNode } from "react";
import { BluebirdLayout } from "@/components/bluebird/BluebirdLayout";
import { HomeFooter, HomeMasthead } from "@/components/bluebird/Chrome";
import { InstagramFeed } from "@/components/bluebird/InstagramFeed";
import {
  BikeGrid,
  Hero,
  PromiseStrip,
  SavingsBand,
  ShopBand,
  Testimonials,
  type GridBike,
} from "@/components/bluebird/HomeSections";
import { DESIGN_FIXTURE_BIKES } from "@/lib/copy/home";
import { photosFor } from "@/lib/photos";
import type { Bike } from "@/types/bike";
import { CatalogNotice, type CatalogStatus } from "@/components/bluebird/CatalogNotice";

interface HomeProps {
  featured?: Bike[];
  /** True only in development, only with ?__designfixture=1. See PagesController#home. */
  designFixture?: boolean;
  catalog_status?: CatalogStatus;
}

const money = (cents: number) =>
  "$" + Math.round(cents).toLocaleString("en-US");

/**
 * A real catalog bike, in the shape the grid wants.
 *
 * The photo labels describe the shot that belongs in each slot until real
 * product photography lands (it is being sourced in a parallel session). They
 * are captions on a placeholder, not claims about the bike.
 */
const toGridBike = (bike: Bike): GridBike => {
  const photos = photosFor(bike.handle);
  const candidates = [
    photos.studio,
    photos.lifestyleHero,
    bike.image_url ? {
      url: bike.image_url,
      altText: bike.image_alt,
      width: bike.image_width,
      height: bike.image_height,
      ground: "#ffffff",
      fit: "contain" as const,
      srcSet: undefined,
    } : undefined,
  ].filter((candidate): candidate is NonNullable<typeof candidate> => Boolean(candidate?.url));
  const primary = candidates[0];
  const hover = (photos.gallery || []).find(
    (photo) => photo.kind === "lifestyle" && photo.url !== primary?.url,
  );

  return {
    name: bike.title,
    price: money(bike.price),
    shot: `photo — side profile, ${bike.title}`,
    shotInUse: `photo — same bike, in use`,
    available: bike.in_stock,
    sold: !bike.in_stock,
    href: `/bikes/${bike.handle}`,
    imageUrl: primary?.url,
    imageAlt: primary?.altText || bike.title,
    imageSrcSet: primary?.srcSet,
    imageWidth: primary?.width,
    imageHeight: primary?.height,
    imageGround: primary?.ground,
    imageFit: primary?.fit,
    imageFallbacks: candidates.slice(1),
    hoverUrl: hover?.url,
    hoverSrcSet: hover?.srcSet,
  };
};

function Home({ featured = [], designFixture = false, catalog_status }: HomeProps) {
  // The comp's six placeholder bikes, so script/design_diff.mjs can diff the
  // grid's layout instead of skipping it. Never reachable in production.
  const bikes: GridBike[] = designFixture
    ? DESIGN_FIXTURE_BIKES.map((b) => ({ ...b, href: "/bikes" }))
    : featured.map(toGridBike);

  const frozen =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).has("__freeze");

  return (
    <>
      <HomeMasthead>
        <Hero frozen={frozen} />
      </HomeMasthead>

      {/* The <main> landmark. A screen-reader user's "skip to content" jump
          lands here, and without it the only landmarks on the page are a banner
          and a footer with everything else unlabelled. <main> is display:block
          with no margin, so it wraps the same boxes at the same positions —
          verified by script/design_diff.mjs, which still reports 0%. */}
      <main>
      <PromiseStrip />
      <CatalogNotice status={catalog_status} />
      <BikeGrid bikes={bikes} />
      <SavingsBand />
      <ShopBand />
      <Testimonials />
      <InstagramFeed />
      </main>

      <HomeFooter />
    </>
  );
}

Home.layout = (page: ReactNode) => <BluebirdLayout>{page}</BluebirdLayout>;

export default Home;
